import React, { useEffect, useState } from 'react';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { usePermissionCheck } from '../../../../../core/hooks/usePermissionCheck';
import { Organization } from 'types';
import { Button } from 'components/ui/button';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button as MuiButton, CircularProgress, Box } from '@mui/material';
import ShowOrganisationSetting from './ShowOrganisationSettings/ShowOrganisationSetting';
import styles from '../GeneralSettings.module.scss';
import EditOrganisationSettings from './EditOrganisationSettings/EditOrganisationSetting';
import CreateOrganisationSettings from './CreateOrganisationSettings';
import { useOrganizationCrud } from 'core/hooks/useOrganizationCrud';
import { useResetSettingsScroll } from '../../../utils/resetSettingsScroll';
import useSettingsStore from 'store/useSettingsStore';

export interface OrganizationSettingsProps {
	handleFocusChange?: (focus: 'organization' | 'workplace' | 'user' | null) => void;
}

function OrganizationSettings(props: OrganizationSettingsProps) {
	const { handleFocusChange } = props;
	const { hasEditPermission, hasViewPermission } = usePermissionCheck();
	const canViewOrganization = hasViewPermission('Organization');
	const canEditOrganization = hasEditPermission('Organization');
	
	// Use the custom hook for organization CRUD operations
	const {
		organizations,
		loading: organizationsLoading,
		error: organizationsError,
		fetchOrganizations,
		deleteOrganization: apiDeleteOrganization,
	} = useOrganizationCrud();
	const fetchUsers = useSettingsStore((s) => s.fetchUsers);

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	useResetSettingsScroll(viewMode);
	const [selectedorganization, setSelectedOrganization] = useState<Organization | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null);

	// Load organizations once into the shared settings store (skips if already cached)
	useEffect(() => {
		fetchOrganizations({
			orderBy: 'id',
			direction: 'ASC',
			limit: 5,
			page: 1,
		}).catch(() => {
			console.log('New API not available, using existing API');
		});
	}, [fetchOrganizations]);

	// Prefetch users for the Organization Administrator picker (shared settings-store cache).
	useEffect(() => {
		const raw = localStorage.getItem('workplace-id');
		let workplaceId: number | null = null;
		if (raw) {
			try {
				const parsed = JSON.parse(raw);
				if (typeof parsed === 'number') workplaceId = parsed;
			} catch {
				const num = Number(raw);
				workplaceId = Number.isNaN(num) ? null : num;
			}
		}

		fetchUsers({
			...(workplaceId != null ? { workplaceId } : {}),
			orderBy: 'lastName',
			direction: 'ASC',
			limit: 1000,
			page: 1,
			active: true,
		}).catch(() => {
			// Picker will retry when Edit Organization opens.
		});
	}, [fetchUsers]);

	const handleBackClick = () => {
		setViewMode('list');
		handleFocusChange(null);
		setSelectedOrganization(null);
	};

	const handleShowClick = (organization: Organization) => {
		console.log(`Show organization ${organization.id}`);
		handleFocusChange('organization');
		setSelectedOrganization(organization);
		setViewMode('show');
	};

	const handleEditClick = (organization: Organization) => {
		if (!canEditOrganization) return;
		handleFocusChange('organization');
		setSelectedOrganization(organization);
		setViewMode('edit');
	};

	const handleConfirmDelete = async () => {
		if (organizationToDelete) {
			try {
				await apiDeleteOrganization(organizationToDelete.id);
				console.log(`Organization ${organizationToDelete.name} deleted successfully`);
			} catch (error) {
				console.error('Error deleting organization:', error);
			}
		}
		setDeleteDialogOpen(false);
		setOrganizationToDelete(null);
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setOrganizationToDelete(null);
	};

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* organizations Card */}
					<div className={styles.workplaceCard}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Organization</h2>
						</div>

					{organizationsError && <div className={styles.errorMessage}>{organizationsError}</div>}

					{organizationsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
					<CircularProgress size={20} color="inherit" />
						</Box>
					) : organizations && organizations.length > 0 ? (
							<div className={styles.tableContainer}>
								<table className={`${styles.workplaceTable} ${styles.organizationTable}`}>
									<thead>
										<tr>
											<th>Name</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>View</th>
											<th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
										</tr>
									</thead>
									<tbody>
										{organizations.slice(0, 5).map((organization: any) => (
											<tr key={organization?.id}>
												<td>{organization.name}</td>
												<td>
													<button
														className={styles.iconButton}
														onClick={() => handleShowClick(organization)}
														title={canViewOrganization ? "View Organization" : "You do not have permission for this action"}
														disabled={!canViewOrganization}
														style={{ opacity: canViewOrganization ? 1 : 0.5, cursor: canViewOrganization ? 'pointer' : 'not-allowed' }}
													>
														<Visibility className={styles.editIcon} />
													</button>
												</td>
												<td>
													<button
														className={styles.iconButton}
														onClick={() => handleEditClick(organization)}
														title={canEditOrganization ? "Edit Organization" : "You do not have permission for this action"}
														disabled={!canEditOrganization}
														style={{ opacity: canEditOrganization ? 1 : 0.5, cursor: canEditOrganization ? 'pointer' : 'not-allowed' }}
													>
														<Edit className={styles.editIcon} />
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : !organizationsLoading && !organizationsError ? (
							<div className={styles.noDataMessage}>No organizations found.</div>
						) : null}
					</div>

					{/* Delete Confirmation Dialog */}
					<Dialog
						open={deleteDialogOpen}
						onClose={handleCancelDelete}
						aria-labelledby="delete-dialog-title"
						aria-describedby="delete-dialog-description"
					>
						<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
						<DialogContent>
							<DialogContentText id="delete-dialog-description">
								Are you sure you want to delete the organization &quot;{organizationToDelete?.name}&quot;? 
								This action cannot be undone.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<MuiButton onClick={handleCancelDelete} color="primary">
								Cancel
							</MuiButton>
							<MuiButton onClick={handleConfirmDelete} color="error" autoFocus>
								Delete
							</MuiButton>
						</DialogActions>
					</Dialog>
				</>
			);
		case 'create':
			return (
				<div className={styles.showWorkplaceContainer}>
					<CreateOrganisationSettings
						backButtonClick={handleBackClick}
					/>
				</div>
			);
		case 'edit':
			if (!canEditOrganization) {
				setViewMode('list');
				return null;
			}
			return (
				<div className={styles.showWorkplaceContainer}>
					{selectedorganization && (
						<EditOrganisationSettings
							organization={selectedorganization}
							handleBackClick={handleBackClick}
						/>
					)}
				</div>
			);
		default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<div className={styles.headerIdentity}>
							<h2 className={styles.formTitle}>{selectedorganization?.name}</h2>
							<p className={styles.headerMeta}>
								Organization details
								{selectedorganization
									? ` · ${localStorage.getItem('BYOD-organizationId') || selectedorganization.id}`
									: ''}
							</p>
						</div>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedorganization && <ShowOrganisationSetting organization={selectedorganization} />}
				</div>
			);
	}
}

export default OrganizationSettings;
